import { Plus, Sparkles } from 'lucide-react';
import { useState } from 'react';

import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';

import { ProactivityExperimentDialog } from './ProactivityExperimentDialog';
import {
  ProactivityExperimentCohortDialogLoader,
  ProactivityExperimentDuplicateDialog,
} from './ProactivityExperimentDialogLoader';
import { ProactivityExperimentsTable } from './ProactivityExperimentsTable';
import {
  useProactivityExperiment,
  useProactivityExperimentOptions,
  useProactivityExperiments,
} from './useProactivityExperiments';

type DialogState =
  | { mode: 'create' }
  | { mode: 'duplicate'; experimentId: string }
  | { mode: 'edit-cohort'; experimentId: string };

export const ProactivityExperimentsPage = () => {
  const experiments = useProactivityExperiments();
  const options = useProactivityExperimentOptions();
  const [dialog, setDialog] = useState<DialogState | null>(null);
  const [expandedExperimentId, setExpandedExperimentId] = useState<
    string | null
  >(null);
  const experimentList = experiments.data ?? [];

  const expandedExperimentDetail = useProactivityExperiment(
    expandedExperimentId ?? '',
    Boolean(expandedExperimentId)
  );

  const duplicateExperimentId =
    dialog?.mode === 'duplicate' ? dialog.experimentId : null;
  const duplicateExperimentDetail = useProactivityExperiment(
    duplicateExperimentId ?? '',
    Boolean(duplicateExperimentId)
  );

  const cohortExperimentId =
    dialog?.mode === 'edit-cohort' ? dialog.experimentId : null;
  const cohortExperimentDetail = useProactivityExperiment(
    cohortExperimentId ?? '',
    Boolean(cohortExperimentId)
  );

  return (
    <>
      <Card>
        <CardHeader className="flex flex-row flex-wrap items-start justify-between gap-4 space-y-0">
          <section className="space-y-1.5">
            <CardTitle className="flex items-center gap-2">
              <Sparkles className="h-5 w-5" />
              Proactivity Experiments
            </CardTitle>
            <CardDescription>
              Issue a Home card, a direct chat, or a routine to a PostHog
              cohort, and follow what each recipient did with it.
            </CardDescription>
          </section>
          <Button
            onClick={() => setDialog({ mode: 'create' })}
            disabled={!options.data}
          >
            <Plus />
            New experiment
          </Button>
        </CardHeader>
        <CardContent>
          {options.error ? (
            <p role="alert" className="mb-3 text-sm text-destructive">
              {options.error.message}
            </p>
          ) : null}
          {experiments.error ? (
            <p role="alert" className="text-sm text-destructive">
              {experiments.error.message}
            </p>
          ) : experiments.isLoading ? (
            <p className="py-8 text-center text-sm text-muted-foreground">
              Loading...
            </p>
          ) : (
            <ProactivityExperimentsTable
              experiments={experimentList}
              expandedExperimentId={expandedExperimentId}
              onExpandedExperimentIdChange={setExpandedExperimentId}
              expandedExperimentDetail={expandedExperimentDetail}
              onDuplicate={(experiment) =>
                setDialog({ mode: 'duplicate', experimentId: experiment.id })
              }
              onEditCohort={(experiment) =>
                setDialog({ mode: 'edit-cohort', experimentId: experiment.id })
              }
            />
          )}
        </CardContent>
      </Card>
      {dialog?.mode === 'create' && options.data ? (
        <ProactivityExperimentDialog
          mode="create"
          options={options.data}
          onOpenChange={(open) => {
            if (!open) setDialog(null);
          }}
        />
      ) : null}
      {dialog?.mode === 'edit-cohort' && options.data ? (
        <ProactivityExperimentCohortDialogLoader
          detail={cohortExperimentDetail}
          options={options.data}
          onOpenChange={(open) => {
            if (!open) setDialog(null);
          }}
        />
      ) : null}
      {dialog?.mode === 'duplicate' && options.data ? (
        <ProactivityExperimentDuplicateDialog
          experimentId={dialog.experimentId}
          detail={duplicateExperimentDetail}
          options={options.data}
          onOpenChange={(open) => {
            if (!open) setDialog(null);
          }}
        />
      ) : null}
    </>
  );
};
